import React, { useState, useCallback } from 'react';
import { useTon } from '../TelegramTonProvider';
import { useTelegram } from '../TelegramProvider';
import { useLilapeBalance } from '../hooks/useLilapeBalance';
import { useGameBalance } from '../hooks/useGameBalance';
import { formatLilape } from '../config/gameConfig';

const DEPOSIT_OPTIONS = [
  { lilape: 500_000_000, label: '500M' },
  { lilape: 1_000_000_000, label: '1B' },
  { lilape: 5_000_000_000, label: '5B' },
];

export const DepositWithdrawPanel = ({ onClose, gameBalanceHook }) => {
  const { connected, sendTransaction, address, connect } = useTon();
  const { showAlert } = useTelegram();
  const { balance: walletBalance, refetch: refetchWallet } = useLilapeBalance(address);

  // Use provided hook or create own
  const ownHook = useGameBalance(address, sendTransaction);
  const { gameBalance, depositing, withdrawing, deposit, withdraw, refetch } = gameBalanceHook || ownHook;

  const [depositAmount, setDepositAmount] = useState(1_000_000_000);

  const handleDeposit = useCallback(async (amount) => {
    if (!connected) {
      if (connect) connect();
      return;
    }
    const success = await deposit(amount);
    if (success) {
      showAlert?.(`✅ Deposited ${formatLilape(amount)} LILAPE to game!`);
      refetchWallet?.();
      refetch?.();
      onClose?.();
    } else {
      showAlert?.(`❌ Deposit failed`);
    }
  }, [connected, connect, deposit, showAlert, onClose]);

  const handleWithdraw = useCallback(async () => {
    if (gameBalance <= 0) {
      showAlert?.('No balance to withdraw');
      return;
    }
    const success = await withdraw(gameBalance);
    if (success) {
      showAlert?.(`✅ Withdrew ${formatLilape(gameBalance)} LILAPE to wallet!`);
      refetchWallet?.();
      refetch?.();
      onClose?.();
    } else {
      showAlert?.(`❌ Withdrawal failed`);
    }
  }, [gameBalance, withdraw, showAlert, onClose]);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.85)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}>
      <div style={{
        background: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%)',
        borderRadius: 16, padding: 20, width: '100%', maxWidth: 340,
        border: '1px solid rgba(255,255,255,0.1)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: '#fff', fontSize: 18 }}>💰 Deposit / Withdraw</h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: '#888', fontSize: 24, cursor: 'pointer'
          }}>×</button>
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1, background: 'rgba(255,255,255,0.05)', borderRadius: 10, padding: 12 }}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Wallet</div>
            <div style={{ fontSize: 16, color: '#10B981', fontWeight: 700 }}>{formatLilape(walletBalance || 0)}</div>
          </div>
          <div style={{ flex: 1, background: 'rgba(255,255,255,0.05)', borderRadius: 10, padding: 12 }}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Game</div>
            <div style={{ fontSize: 16, color: '#F59E0B', fontWeight: 700 }}>{formatLilape(gameBalance || 0)}</div>
          </div>
        </div>

        {!connected ? (
          <button onClick={connect} style={{
            width: '100%', padding: 14, borderRadius: 10,
            background: 'linear-gradient(135deg, #0088CC, #0066AA)',
            border: 'none', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer',
          }}>
            💎 Connect Wallet
          </button>
        ) : (
          <>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>Deposit Amount</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[...DEPOSIT_OPTIONS, { lilape: walletBalance || 0, label: 'MAX', isMax: true }].map(opt => {
                  const canAfford = (walletBalance || 0) >= opt.lilape && opt.lilape > 0;
                  const isSelected = opt.isMax ? depositAmount === walletBalance : depositAmount === opt.lilape;
                  return (
                    <button
                      key={opt.label}
                      onClick={() => setDepositAmount(opt.lilape)}
                      disabled={!canAfford}
                      style={{
                        flex: 1, padding: '8px 4px', borderRadius: 8,
                        background: isSelected ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.05)',
                        border: `2px solid ${isSelected ? '#10B981' : 'transparent'}`,
                        color: canAfford ? (isSelected ? '#10B981' : '#fff') : '#555',
                        fontSize: 13, fontWeight: 600, cursor: canAfford ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => handleDeposit(depositAmount)}
                disabled={depositing || !connected || (walletBalance || 0) < depositAmount}
                style={{
                  flex: 1, padding: 14, border: 'none', borderRadius: 10,
                  background: (walletBalance || 0) >= depositAmount ? 'linear-gradient(135deg, #10B981, #059669)' : '#333',
                  color: (walletBalance || 0) >= depositAmount ? '#fff' : '#666',
                  fontSize: 14, fontWeight: 700, cursor: (walletBalance || 0) >= depositAmount ? 'pointer' : 'not-allowed',
                }}
              >
                {depositing ? '⏳...' : '💰 DEPOSIT'}
              </button>
              <button
                onClick={handleWithdraw}
                disabled={withdrawing || gameBalance <= 0}
                style={{
                  flex: 1, padding: 14, border: 'none', borderRadius: 10,
                  background: gameBalance > 0 ? 'linear-gradient(135deg, #F59E0B, #D97706)' : '#333',
                  color: gameBalance > 0 ? '#fff' : '#666',
                  fontSize: 14, fontWeight: 700, cursor: gameBalance > 0 ? 'pointer' : 'not-allowed',
                }}
              >
                {withdrawing ? '⏳...' : '💸 WITHDRAW'}
              </button>
            </div>
          </>
        )}

        <div style={{ fontSize: 11, color: '#666', textAlign: 'center', marginTop: 12 }}>
          80% pool · 20% burn 🔥
        </div>
      </div>
    </div>
  );
};

export const DepositButton = ({ gameBalance, onClick }) => (
  <button
    onClick={onClick}
    style={{
      padding: '8px 16px', borderRadius: 8,
      background: 'linear-gradient(135deg, #10B981, #059669)',
      border: 'none', color: '#fff', fontSize: 12, fontWeight: 700,
      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
    }}
  >
    💰 {gameBalance > 0 ? formatLilape(gameBalance) : 'Deposit'}
  </button>
);
